/**
 * State channel — listens for `state_init`, `state_delta`, and
 * `state_heartbeat` messages on the existing `/ws/ticks` WebSocket and
 * routes them into the Zustand store.
 *
 * Phase E hardening:
 *   - All 12 domains now in KNOWN_DOMAINS (was 5)
 *   - Per-domain seq gap detection → REST resync for that domain only
 *   - Connection-state tracking (last message ts, missed heartbeats)
 *   - On reconnect: re-fire bootstrap GET to repopulate the store
 *
 * The actual WS lifecycle is managed by useLiveTicks; this module only
 * handles message routing + the bootstrap-on-reconnect helper.
 */
import { useStateStore, type DomainKey } from "@/state/store";
import { bootstrapState } from "@/state/bootstrap";
import { mirrorDomainToQueryCache } from "@/state/queryCacheMirror";

// 2026-04-28 (audit T3#21): added 4 domains added 2026-04-27 (holdings,
// charges, system_toggles, system_extras). Without these, backend
// state_init/state_delta for these keys was silently dropped by the runtime
// type-guard, falling back to legacy REST polls — defeating the consolidation.
const KNOWN_DOMAINS = new Set<DomainKey>([
  "portfolio", "positions", "orders", "trades", "signals",
  "indices", "system_health", "auth_status", "alerts",
  "settings_meta", "gtt", "scheduler_status", "metrics",
  "feature_flags", "dispatcher",
  "holdings", "charges", "system_toggles", "system_extras",
]);

/** Type guards for WS messages. */
type StateInitMsg = { type: "state_init"; key: DomainKey; data: Record<string, unknown>; seq: number };
type StateDeltaMsg = { type: "state_delta"; key: DomainKey; delta: Record<string, unknown>; seq: number };
type StateHeartbeatMsg = { type: "state_heartbeat"; seqs: Record<string, number> };
type StateCaughtUpMsg = { type: "state_caught_up"; seqs: Record<string, number>; throttled_after_secs?: number };

function isStateInit(m: unknown): m is StateInitMsg {
  return !!m && typeof m === "object"
    && (m as { type?: unknown }).type === "state_init"
    && typeof (m as { key?: unknown }).key === "string"
    && KNOWN_DOMAINS.has((m as { key: string }).key as DomainKey);
}

function isStateDelta(m: unknown): m is StateDeltaMsg {
  return !!m && typeof m === "object"
    && (m as { type?: unknown }).type === "state_delta"
    && typeof (m as { key?: unknown }).key === "string"
    && KNOWN_DOMAINS.has((m as { key: string }).key as DomainKey);
}

function isStateHeartbeat(m: unknown): m is StateHeartbeatMsg {
  return !!m && typeof m === "object"
    && (m as { type?: unknown }).type === "state_heartbeat";
}

function isStateCaughtUp(m: unknown): m is StateCaughtUpMsg {
  return !!m && typeof m === "object"
    && (m as { type?: unknown }).type === "state_caught_up";
}

/** Phase E: connection-state tracking. */
let lastMessageTs = 0;
let lastHeartbeatTs = 0;
const STALE_THRESHOLD_MS = 30_000;

export function getStateChannelHealth(): {
  lastMessageMs:    number;
  lastHeartbeatMs:  number;
  isStale:          boolean;
} {
  const now = Date.now();
  return {
    lastMessageMs:   lastMessageTs ? now - lastMessageTs : -1,
    lastHeartbeatMs: lastHeartbeatTs ? now - lastHeartbeatTs : -1,
    isStale:         lastMessageTs > 0 && (now - lastMessageTs) > STALE_THRESHOLD_MS,
  };
}

/** Fetch a fresh snapshot for a domain (used for sequence-gap recovery). */
async function resyncDomain(key: DomainKey): Promise<void> {
  try {
    const token = localStorage.getItem("mct_token");
    const resp = await fetch(`/api/state/${key}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      // 404 means STATE_AGGREGATOR_ENABLED=False on the server; nothing to do.
      return;
    }
    const data = await resp.json();
    const seq = typeof data?._seq === "number" ? data._seq : 0;
    useStateStore.getState().applyInit(key, data, seq);
    try { mirrorDomainToQueryCache(key, data); } catch { /* ignore */ }
  } catch (e) {
    // Network blip — UI stays stale until WS reconnects + sends new state_init.
    console.debug(`[stateChannel] resync ${key} failed:`, e);
  }
}

/**
 * Public API: route a single WS message into the store. Call this from
 * any WS message handler. Unknown message types are ignored (returns false
 * so the original handler can still process them).
 */
export function handleStateMessage(msg: unknown): boolean {
  // Track activity timestamp regardless of whether we handled the message.
  lastMessageTs = Date.now();

  if (isStateInit(msg)) {
    useStateStore.getState().applyInit(msg.key, msg.data, msg.seq);
    // Mirror on init only — primes the RQ cache for legacy useQuery
    // subscribers when the WS first connects (or reconnects). This is
    // enough for cold-load freshness without the per-delta thrash that
    // crashed the dashboard on 2026-04-24 (React #185 loop in
    // StoreSync.tsx where the composed `portfolio` object's reference
    // changes on every render).
    try { mirrorDomainToQueryCache(msg.key, msg.data); } catch { /* ignore */ }
    return true;
  }
  if (isStateDelta(msg)) {
    const ok = useStateStore.getState().applyDelta(msg.key, msg.delta, msg.seq);
    if (!ok) {
      // Sequence gap detected — resync this domain only (not all 12).
      void resyncDomain(msg.key);
    }
    // NOTE: intentionally NOT mirroring deltas to the RQ cache — that
    // path needs a structural-equality guard before it can be safely
    // re-enabled. Components that read via useStateStore() see deltas
    // immediately (Zustand path); components on the legacy useQuery
    // path see fresh data on cold load + on WS reconnect (state_init).
    return true;
  }
  // 2026-04-27: I6 — handle state_caught_up. Backend sends this in lieu of
  // a full state_init burst when a client reconnects within the per-IP
  // throttle window (default 30s). Same gap-detection logic as the
  // heartbeat path: if our local seq for any domain is BEHIND the server's,
  // we missed a delta during the throttle — resync that domain.
  if (isStateCaughtUp(msg)) {
    // Treat as a heartbeat for liveness purposes — without this, the
    // connection-health gauge would show stale until the next true
    // state_heartbeat (10s later).
    lastHeartbeatTs = Date.now();
    try {
      const state = useStateStore.getState();
      for (const [k, serverSeq] of Object.entries(msg.seqs)) {
        if (KNOWN_DOMAINS.has(k as DomainKey)) {
          const localSeq = state[k as DomainKey]?.lastSeq ?? 0;
          if (serverSeq > localSeq) {
            void resyncDomain(k as DomainKey);
          }
        }
      }
    } catch {
      /* ignore */
    }
    return true;
  }

  if (isStateHeartbeat(msg)) {
    lastHeartbeatTs = Date.now();
    // Phase E: server-pushed heartbeat with current seqs lets us verify
    // we're still in sync without an actual data message. If our local
    // seq for any domain is BEHIND the server's, we missed a delta —
    // resync that domain.
    try {
      const localSeqs: Record<string, number> = {};
      const state = useStateStore.getState();
      for (const k of KNOWN_DOMAINS) {
        localSeqs[k] = state[k]?.lastSeq ?? 0;
      }
      for (const [k, serverSeq] of Object.entries(msg.seqs)) {
        if (KNOWN_DOMAINS.has(k as DomainKey) && serverSeq > (localSeqs[k] ?? 0)) {
          void resyncDomain(k as DomainKey);
        }
      }
    } catch {
      /* ignore */
    }
    return true;
  }
  return false;
}

/**
 * Mark all slices stale on WS disconnect. Phase E: also kicks off a fresh
 * bootstrap fetch so that when the user comes back online + WS reconnects,
 * the store is already pre-populated (bootstrap is fire-and-forget; if the
 * network is still down it'll fail gracefully).
 */
export function onStateChannelDisconnect(): void {
  useStateStore.getState().markStale();
}

/** Phase E: called by useLiveTicks on WS RECONNECT. Re-bootstraps state from
 * the single endpoint, then WS state_init/delta will resume normally. */
export async function onStateChannelReconnect(): Promise<void> {
  try {
    const ok = await bootstrapState();
    if (!ok) {
      // Bootstrap failed (e.g. STATE_AGGREGATOR_ENABLED=false). Fall back to
      // per-domain REST resync — slower but works for individual domains.
      await fetchAllDomainsViaRest();
    }
  } catch (e) {
    console.warn("[stateChannel] reconnect bootstrap failed:", e);
  }
}

/**
 * One-shot REST fetch of all known domains. Used as a fallback when the
 * bootstrap endpoint isn't available.
 */
export async function fetchAllDomainsViaRest(): Promise<void> {
  await Promise.all(Array.from(KNOWN_DOMAINS).map(resyncDomain));
}
